import React from 'react';
import { BlockMath, InlineMath } from 'react-katex';
import DoggoRobot from '../assets/doggo_robot.png';
import { Footnote, FootnoteRef, FootNoteRefProps_t } from '../components/FootNote';


export const KoopmanCBFPage: React.FC<{}> = () => {

    const references: Array<FootNoteRefProps_t> = React.useMemo(() => [
        { id: "cbf", ref: "https://arxiv.org/pdf/1903.11199" },
        { id: "cbf_mpc", ref: "https://arxiv.org/pdf/2007.11718"},
        { id: "koopman", ref: "https://arxiv.org/pdf/1805.07472"},
        { id: "koopman_legged", ref: "https://arxiv.org/pdf/2411.14321"}, 
        { id: "safety_gym", ref: "https://openai.com/index/safety-gym/"},
        { id: "verification", ref: "https://arxiv.org/pdf/2210.08339"},
        { id: "distillation", ref: "https://arxiv.org/pdf/1503.02531"},
    ], []);

    // This gets the reference number that we render next to the footnote.
    // It just finds the index of the footnote with the given id in the list.
    const f = React.useCallback((id: string) => {
    let index = references.findIndex(ref => ref.id === id);
    if (index === -1) {
        throw Error(`could not find reference: ${id} in list`);
    }
    return index + 1;
    }, [references]);
    
    return (
        <main>
            <h1>Control Barrier Functions with Learned Koopman Operators and Model-Predictive Control</h1>
            <p>
                Recent work has shown that control-barrier functions (CBFs)<Footnote f={f} id="cbf" /> can be used to enforce safety constraints with model-predictive controllers (MPC)<Footnote f={f} id={"cbf_mpc"}/>.
            </p>
            <p>
                For MPC problems to be solved efficiently, the model of the system dynamics must be convex and 
                usually practitioners use a linear model. This has prevented MPC from being used on high-dimensional 
                non-linear systems.
            </p> 
            
            <p>
                Koopman Operators are a function that maps the system state to a higher-dimensional space where 
                the system dynamics are linear. Recent work has shown that this mapping can be learned which allows MPC to be used 
                on non-linear systems like fluids<Footnote id="koopman" f={f}/> and walking 
                robots<Footnote id="koopman_legged" f={f}/>.
            </p>
            <p>
                Can we enforce safety constraints with learned Koopman Operators and MPC? 
            </p>
            <p>
                This problem is hard because we perform MPC on this higher-dimensional state but we would like to 
                constrain the lower-dimensional state. For example if we constrain 
                the <InlineMath>x</InlineMath> and <InlineMath>y</InlineMath> state dimensions in the original state space,
                what does that correspond to in the higher-dimensional state? Since this mapping is a learned neural network,
                the higher-dimensional state is inscrutable.
            </p>
            <h2>Idea 1: Partial Koopman Operators</h2>
            <p>
                Often when we restrict the state with safety constraints, we only restrict a subset of the 
                state dimensions. For example we may restrict a humanoid robot from entering a circle 
                which restricts the <InlineMath>x</InlineMath> and <InlineMath>y</InlineMath> state dimensions
                but not the joint angles.
            </p>
            <div style={{ textAlign: 'center' }}>
                <img src={DoggoRobot} alt="Doggo Robot" style={{ width: '50%' }} />
                <p style={{fontSize: "small"}}>
                    This robot from the OpenAI Safety Gym<Footnote f={f} id={"safety_gym"} /> has more than 30 dimensions of 
                    state but only 2 are restricted to stay out of the blue circles.
                </p>
            </div>
            <p>
                Rather than mapping the whole state through a neural network, could we just map the part of the state we aren't constraining?
            </p>
            <p>Normal Koopman Operator</p>
            <BlockMath math="f_{\text{learned mapping}}(\begin{bmatrix} x \\ y \\ \dot{x} \\ \dot{y} \\ \theta \\ \phi \\ \dot{\theta} \\ \dot{\phi} \end{bmatrix}) \rightarrow \begin{bmatrix} z_{1} \\ z_{2} \\ ... \\ z_{n} \end{bmatrix} = \bold{z}"/>
            <p>Partial Koopman Operator</p>
            <BlockMath math="
            \hspace{-0.3cm} \begin{bmatrix} x \\ y \end{bmatrix} \rightarrow \\
            f_{\text{learned mapping}}(\begin{bmatrix} \dot{x} \\ \dot{y} \\ \theta \\ \phi \\ \dot{\theta} \\ \dot{\phi} \end{bmatrix}) \rightarrow \begin{bmatrix} x \\ y \\ z_{1} \\ z_{2} \\ ... \\ z_{n} \end{bmatrix} = \bold{z}_{\text{partial}}"/>
            <p>
                Now we can do MPC on <InlineMath math="\bold{z_{\text{partial}}}"/> and the first two dimensions correpond 
                to <InlineMath>x</InlineMath> and <InlineMath>y</InlineMath> in our original state. So we could use CBFs to constrain those first two dimensions.
            </p>
            <p>
                Also note that dynamics are non-linear in the joint angles and linear in the <InlineMath math="(x,y)"/> position
                on the plane. So the fact that we aren't mapping the <InlineMath math="(x,y)"/> position to a higher-dimensional state 
                would not affect the accuracy of our learned Koopman Operator's approximation of the true dynamics.
            </p>
            <h2>Idea 2: Neural Network Reachability</h2>
            <p>
                If we could map our Avoid Set <InlineMath math="\mathcal{A}"/> from the original state space to find the Avoid Set 
                in the higher dimensional space <InlineMath math="\mathcal{A}_{f}"/>.
                <BlockMath math="\mathcal{A}_{f} = \{f_{\text{learned mapping}}(x) \ \forall x \in \mathcal{A} \}"/>
                Then we could use CBFs with our avoid set as <InlineMath math="\mathcal{A}_{f}"/>.
            </p>
            <p>
                This subdomain is called neural network verification and specifically forward neural network reachability and there is existing 
                work<Footnote id="verification" f={f}/> and code for networks with ReLU activations.
            </p>
            <p>
                It is more efficient for smaller networks, so once we've learned <InlineMath math="f_{\text{learned mapping}}"/>, we could
                distill<Footnote f={f} id="distillation"/> it into a smaller model.
            </p>
            <section id="footnotes">
            {references.map((item, i) => (
                <FootnoteRef id={item.id} text={item.text} ref={item.ref} number={i+1} />
                ))
            }
            </section>
        </main>
    )
};
